<script setup lang="ts">
import { computed, inject, type Ref } from 'vue'
import { useUserStore } from '@/stores/user'
import type { Room } from '@/models/Room'
import { ScanMode } from '@/models/Scan'
import type { Container } from '@/models/Container'

const DEFAULT_RESOLUTION = 1280

const userStore = useUserStore()
const user = userStore.authenticatedUser

const room = inject('room') as Ref<Room | null>
const container = inject('container') as Ref<Container | null>
const picture = inject('picture') as Ref<string | null>

const zebra = user?.scanMode === ScanMode.Zebra
const scanned = computed(() => room.value || container.value)

const onPictureSelected = (event: Event) => {
  const input = event.target as HTMLInputElement
  if (!input.files || input.files.length === 0) return

  const reader = new FileReader()
  reader.onload = (e) => {
    const dataURL = e.target?.result as string

    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      const isLandscape = img.width > img.height
      const newWidth = isLandscape
        ? img.width * (DEFAULT_RESOLUTION / img.height)
        : DEFAULT_RESOLUTION
      const newHeight = isLandscape
        ? DEFAULT_RESOLUTION
        : img.height * (DEFAULT_RESOLUTION / img.width)

      canvas.width = newWidth
      canvas.height = newHeight

      ctx?.drawImage(img, 0, 0, newWidth, newHeight)

      const resizedDataURL = canvas.toDataURL('image/jpeg', 0.9)
      console.log('Picture data URL:', resizedDataURL)

      picture.value = resizedDataURL
    }

    img.src = dataURL
  }

  reader.readAsDataURL(input.files[0])
}
</script>

<template>
  <div class="actions">
    <RouterLink :class="{ action: true, active: scanned }" :to="{ name: 'report-incident-scan' }">
      <img src="../assets/zebra-incident-barcode-active.svg" v-if="zebra && scanned" />
      <img src="../assets/zebra-incident-barcode.svg" v-else-if="zebra" />
      <img src="../assets/incident-barcode-active.svg" v-else-if="scanned" />
      <img src="../assets/incident-barcode.svg" v-else />
      <span>Identifier un local/bac</span>
      <p class="subtitle" v-if="room">Local : {{ room.code }}</p>
      <p class="subtitle" v-if="container">Bac : {{ container.code }}</p>
    </RouterLink>
    <label :class="{ action: true, active: picture }">
      <template v-if="picture">
        <img src="../assets/incident-picture-active.svg" />
        <span>Modifier la photo</span>
      </template>
      <template v-else>
        <img src="../assets/incident-picture.svg" />
        <span>Ajouter une photo</span>
      </template>
      <input
        name="incident_picture"
        type="file"
        accept="image/*"
        capture="environment"
        @change="onPictureSelected"
      />
    </label>
  </div>
</template>

<style scoped lang="scss">
.actions {
  display: flex;
  justify-content: space-between;
  align-items: start;
  align-self: flex-end;
  width: 100%;
  height: 20dvh;
  margin-top: 2dvh;
  margin-bottom: 4dvh;

  .action {
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    background: none;
    border: none;
    padding: 0 2%;

    &.active {
      color: #ffbd00;
    }

    img {
      height: min(8vh, 55px);
    }

    span {
      margin-top: 1dvh;
      text-transform: uppercase;
      font-weight: 600;
      line-height: initial;
    }

    .subtitle {
      margin: 0;
      font-style: italic;
      font-size: 15px;
    }

    input[type='file'] {
      display: none;
    }
  }
}
</style>
